import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { loadStripe } from '@stripe/stripe-js'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import Checkbox from '../components/form/Checkbox'
import FieldGroup from '../components/form/FieldGroup'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import { flattenErrorCount } from '../lib/validators'
import { isValidationDisabled } from '../lib/validationMode'
import { apiPost } from '../lib/api'

const STEP_ID = 'managedBilling'

const packageSchema = z.object({
  internalName: z
    .string({ invalid_type_error: 'Give this package an internal name' })
    .trim()
    .min(2, 'Give this package an internal name')
    .max(60, 'Keep the internal name under 60 characters'),
  displayName: z
    .string({ invalid_type_error: 'Give this package a display name' })
    .trim()
    .min(2, 'Give this package a display name')
    .max(60, 'Keep the display name under 60 characters'),
  monthlyPrice: z.coerce
    .number({ invalid_type_error: 'Set a monthly price' })
    .min(1, 'Set a monthly price')
    .max(100000, 'Monthly price cannot exceed $100,000'),
  annualPrice: z.coerce
    .number({ invalid_type_error: 'Set an annual price' })
    .min(1, 'Set an annual price')
    .max(100000, 'Annual price cannot exceed $100,000'),
})

const strictSchema = z.object({
  packages: z.object({
    female: packageSchema,
    male: packageSchema,
  }),
})
const looseSchema = z.object({
  packages: z.any(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

const PLACEHOLDERS = {
  female: {
    internalName: 'Female BHRT package',
    displayName: 'Female BHRT package',
    monthlyPrice: '200',
    annualPrice: '2,000',
  },
  male: {
    internalName: 'Male BHRT package',
    displayName: 'Male BHRT package',
    monthlyPrice: '250',
    annualPrice: '2,500',
  },
}

let _stripePromise = null
function getStripe() {
  if (_stripePromise) return _stripePromise
  const PK =
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_BRITE ||
    import.meta.env.VITE_STRIPE_PUBLIC_KEY
  if (!PK) return Promise.resolve(null)
  _stripePromise = loadStripe(PK)
  return _stripePromise
}

function PriceInput({ id, value, onChange, placeholder, hasError }) {
  const [focused, setFocused] = useState(false)
  const [raw, setRaw] = useState('')

  const hasNumber = typeof value === 'number' && !Number.isNaN(value)
  const display = focused
    ? raw
    : hasNumber
      ? value.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : ''

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      data-lpignore="true"
      data-form-type="other"
      className={`field-input${hasError ? ' has-error' : ''}`}
      placeholder={placeholder}
      value={display}
      onFocus={() => {
        setRaw(hasNumber ? String(value) : '')
        setFocused(true)
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '')
        setRaw(digits)
        onChange(digits === '' ? undefined : Number(digits))
      }}
    />
  )
}

function PackageRow({ control, errors, group, label }) {
  const err = errors.packages?.[group] || {}
  const path = (f) => `packages.${group}.${f}`
  const ph = PLACEHOLDERS[group]

  const textField = (key, labelText) => (
    <FieldGroup name={path(key)}>
      <Field label={labelText} htmlFor={path(key)} error={err[key]}>
        <Controller
          name={path(key)}
          control={control}
          render={({ field }) => (
            <input
              id={path(key)}
              type="text"
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
              className={`field-input${err[key] ? ' has-error' : ''}`}
              placeholder={ph[key]}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </Field>
    </FieldGroup>
  )

  const priceField = (key, labelText) => (
    <FieldGroup name={path(key)}>
      <Field label={labelText} htmlFor={path(key)} error={err[key]}>
        <div className="input-wrap">
          <span className="currency-prefix" aria-hidden="true">$</span>
          <Controller
            name={path(key)}
            control={control}
            render={({ field }) => (
              <PriceInput
                id={path(key)}
                value={field.value}
                onChange={field.onChange}
                placeholder={ph[key]}
                hasError={!!err[key]}
              />
            )}
          />
        </div>
      </Field>
    </FieldGroup>
  )

  return (
    <Card title={label}>
      <FieldRow cols={2}>
        {textField('internalName', 'Internal name')}
        {textField('displayName', 'Display name')}
      </FieldRow>
      <FieldRow cols={2}>
        {priceField('monthlyPrice', 'Monthly')}
        {priceField('annualPrice', 'Annual')}
      </FieldRow>
    </Card>
  )
}

// Lite mode (/docther) only — small pill at the top of Step 3 hinting that
// content extends below the fold. Listens to the .form-shell scroll container
// (window.scrollY is always 0 in this layout — see StepContainer.jsx) and
// hides itself once the user scrolls past 80px.
function ScrollHint() {
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    const shell = document.querySelector('.form-shell')
    if (!shell) return
    const onScroll = () => {
      if (shell.scrollTop > 80) setVisible(false)
    }
    shell.addEventListener('scroll', onScroll, { passive: true })
    return () => shell.removeEventListener('scroll', onScroll)
  }, [])
  if (!visible) return null
  return (
    <div className="scroll-hint" role="presentation">
      <span className="scroll-hint-text">Scroll for what's included</span>
      <span className="scroll-hint-chevron" aria-hidden="true">▼</span>
    </div>
  )
}

export default function Step3ManagedBilling() {
  const step = STEPS.find((s) => s.id === STEP_ID)
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const personalization = useOnboardingStore((s) => s.personalization)
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  // Stripe processing fee — personalization can override the default 2.99%.
  // Treats 0 / null / negative as "use default".
  const DEFAULT_STRIPE_FEE_PCT = 2.99
  const stripeFeePct =
    typeof personalization?.stripeProcessingFeePct === 'number' &&
    personalization.stripeProcessingFeePct > 0
      ? personalization.stripeProcessingFeePct
      : DEFAULT_STRIPE_FEE_PCT
  const stripeFeePctDisplay = Number.isInteger(stripeFeePct)
    ? `${stripeFeePct}%`
    : `${stripeFeePct.toFixed(2).replace(/\.?0+$/, '')}%`
  const exampleNet = (250 * (1 - stripeFeePct / 100)).toFixed(2)
  const isDiscountedFee = stripeFeePct !== DEFAULT_STRIPE_FEE_PCT
  const defaultExampleNet = (250 * (1 - DEFAULT_STRIPE_FEE_PCT / 100)).toFixed(2)

  const [mbEnabled, setMbEnabled] = useState(persisted?.mbEnabled !== false)
  const [triggerMode, setTriggerMode] = useState(
    persisted?.triggerMode || 'immediate',
  )
  const [achBusy, setAchBusy] = useState(false)
  const [achError, setAchError] = useState('')

  const achConnected = persisted?.achAccount || null

  const defaultValues = useMemo(() => {
    const pkgDefault = (side) => ({
      internalName: persisted?.packages?.[side]?.internalName ?? '',
      displayName: persisted?.packages?.[side]?.displayName ?? '',
      monthlyPrice: persisted?.packages?.[side]?.monthlyPrice ?? '',
      annualPrice: persisted?.packages?.[side]?.annualPrice ?? '',
    })
    return {
      packages: {
        female: pkgDefault('female'),
        male: pkgDefault('male'),
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })


  const onValid = (data) => {
    const fresh = useOnboardingStore.getState().formData?.[STEP_ID]
    if (!fresh?.achAccount?.accountId) {
      setAchError('Connect your bank below to continue — we need somewhere to send your deposits.')
      pulse()
      return
    }
    updateFormData(STEP_ID, { ...data, mbEnabled: true, triggerMode })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  const submitWhenSkipped = () => {
    updateFormData(STEP_ID, {
      acknowledged: false,
      packages: null,
      mbEnabled: false,
      achAccount: null,
    })
    nextStep()
  }

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    setHandler(() => {
      if (!mbEnabled) {
        submitWhenSkipped()
      } else {
        submit()
      }
    })
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler, mbEnabled])

  useEffect(() => {
    reportErrorCount(mbEnabled ? flattenErrorCount(errors) : 0)
  }, [errors, reportErrorCount, mbEnabled])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connectAch = async () => {
    if (achBusy) return
    setAchBusy(true)
    setAchError('')
    try {
      const fd = useOnboardingStore.getState().formData
      const customerId = fd?.stripe?.stripeCustomerId
      const res = await apiPost('/financial-connections-session', {
        customerId: customerId || undefined,
        practiceName: fd?.details?.practiceName,
        ownerEmail: fd?.owners?.owners?.[0]?.email,
      })
      if (!res.ok || !res.data?.clientSecret) {
        setAchError(
          res.data?.error ||
            "Couldn't start bank connection. Try again or contact support@brite.live.",
        )
        return
      }
      const stripe = await getStripe()
      if (!stripe || typeof stripe.collectFinancialConnectionsAccounts !== 'function') {
        setAchError('Bank connection unavailable in this browser.')
        return
      }
      const result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: res.data.clientSecret,
      })
      if (result.error) {
        setAchError(result.error.message || 'Bank connection cancelled.')
        return
      }
      const account = result.financialConnectionsSession?.accounts?.[0]
      if (!account) {
        setAchError('No bank account selected.')
        return
      }
      const achAccount = {
        accountId: account.id,
        bankName: account.institution_name || account.display_name || 'Bank',
        last4: account.last4 || '',
      }
      // Persist the Stripe customerId returned by the FC session so Step 5's
      // pay-with-saved-bank flow has a customer to charge against. Skip if
      // the backend didn't include one (older builds / edge cases) — the
      // tightened render gate in Step 5 falls back to the standard picker.
      if (res.data?.customerId) {
        updateFormData('stripe', { stripeCustomerId: res.data.customerId })
      }
      updateFormData(STEP_ID, { ...persisted, achAccount })
    } catch (err) {
      setAchError(err.message || 'Bank connection failed.')
    } finally {
      setAchBusy(false)
    }
  }

  const disconnectAch = () => {
    updateFormData(STEP_ID, { ...persisted, achAccount: null })
  }

  return (
    <form
      className={`step-body step-body-mb${liteMode ? ' is-lite' : ''}`}
      onSubmit={(e) => {
        e.preventDefault()
        if (!mbEnabled) submitWhenSkipped()
        else handleSubmit(onValid, onInvalid)()
      }}
      noValidate
    >
      <StepLayout
        question="We take over everything."
        helper="Until now, BRITE handled your charts, visits, and memberships — but payments had to happen somewhere else. That ends here."
        aside={
          <div
            className="np-eyebrow"
            style={{ color: 'var(--teal)' }}
          >
            MANAGED BILLING — NOW LIVE
          </div>
        }
        className="is-wide"
      >
      <Band label="Managed Billing" description="Subscription packages and bank connection">
      <ErrorBanner show={submitAttempted} messages={errorMessages} />
      {liteMode && <ScrollHint />}

      <div className="mb-toggle-strip">
        <Checkbox checked={mbEnabled} onChange={setMbEnabled}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>
            Use Managed Billing
          </span>
        </Checkbox>
        <button
          type="button"
          className="mb-skip-link"
          onClick={() => setMbEnabled(false)}
        >
          Not using Managed Billing? →
        </button>
      </div>

      {mbEnabled && (
        <>
          <div className="step-section">
            <div className="np-eyebrow">HOW IT WORKS</div>
            <div className="np-steps-row">
              <div className="np-step-tile">
                <div className="np-step-num">1</div>
                <div className="np-step-title">Patient points phone at your QR code</div>
                <div className="np-step-body">
                  Already a feature of BRITE. Now wired to billing.
                </div>
              </div>
              <div className="np-step-tile">
                <div className="np-step-num">2</div>
                <div className="np-step-title">App onboards them and collects their card</div>
                <div className="np-step-body">
                  Same BRITE app they already use for visits and titration.
                </div>
              </div>
              <div className="np-step-tile">
                <div className="np-step-num">3</div>
                <div className="np-step-title">We collect on your behalf — and pay you</div>
                <div className="np-step-body">
                  Monthly deposit to your account. Failed cards retry automatically.
                </div>
              </div>
            </div>
          </div>

          <div className="mb-pull-quote">
            "When a patient gets archived, both billings stop at the same time. Bam — no leaks."
          </div>

          <div className="step-section">
            <div className="np-eyebrow">WHAT YOU GET</div>
            <div className="np-benefit-row">
              <div className="np-benefit-tile">
                <div className="np-benefit-icon" aria-hidden="true">⊕</div>
                <div className="np-benefit-title">One system, not two</div>
                <div className="np-benefit-body">
                  No Square, no Stripe, no QuickBooks reconciliation. Care and money in one place.
                </div>
              </div>
              <div className="np-benefit-tile">
                <div className="np-benefit-icon" aria-hidden="true">◎</div>
                <div className="np-benefit-title">Patient archives, billing stops</div>
                <div className="np-benefit-body">
                  No risk of charging a patient who left. Both billings end together, automatically.
                </div>
              </div>
              <div className="np-benefit-tile">
                <div className="np-benefit-icon" aria-hidden="true">◈</div>
                <div className="np-benefit-title">Your name on every invoice</div>
                <div className="np-benefit-body">
                  Patient sees "On behalf of [Your Practice]" — your brand stays front and center. We stay invisible.
                </div>
              </div>
            </div>
          </div>

          <div className="step-section">
            <div className="np-eyebrow">PROCESSING FEE</div>
            <div
              className="mb-pricing-card"
              style={
                isDiscountedFee
                  ? {
                      borderLeft: '3px solid var(--accent)',
                      boxShadow: '-1px 0 12px -8px var(--accent)',
                    }
                  : undefined
              }
            >
              {isDiscountedFee && (
                <div className="personalized-eyebrow">
                  {personalization?.practiceName ? (
                    <>
                      Reserved for{' '}
                      <span className="personalized-eyebrow-name">
                        {personalization.practiceName}
                      </span>
                    </>
                  ) : (
                    'Your rate'
                  )}
                </div>
              )}
              <div className="mb-pricing-rate">
                {isDiscountedFee && (
                  <span className="price-was">2.99%</span>
                )}
                <span
                  style={isDiscountedFee ? { color: 'var(--accent)' } : undefined}
                >
                  {stripeFeePctDisplay}
                </span>
              </div>
              <div className="info-block-note" style={{ marginTop: 6 }}>
                per successful payment
              </div>
              <p className="info-block-note" style={{ marginTop: 14 }}>
                A $250 patient charge nets you{' '}
                {isDiscountedFee && (
                  <span className="price-was">${defaultExampleNet}</span>
                )}
                <span
                  style={
                    isDiscountedFee
                      ? { color: 'var(--accent)', fontWeight: 500 }
                      : undefined
                  }
                >
                  ${exampleNet}
                </span>{' '}
                after processing.
              </p>
              <div className="mb-pricing-includes">
                <div><span>✓</span>Monthly deposit to your bank</div>
                <div><span>✓</span>Failed-card retries handled automatically</div>
                <div><span>✓</span>Reconciliation between BRITE charges and patient payments</div>
                <div><span>✓</span>Compliance reporting</div>
                <div><span>✓</span>No monthly fees, no chargeback fees</div>
              </div>
            </div>
          </div>

          <div className="mb-pull-quote">
            "We reverse the flow — rather than you paying us, we pay you."
          </div>

          <div className="mb-ach-section">
            <div className="mb-ach-eyebrow">REQUIRED — DEPOSIT ACCOUNT</div>
            <h3 className="mb-ach-headline">Where should we send your monthly deposits?</h3>
            <p className="mb-ach-body">
              Connect your business bank account for monthly deposits. Encrypted and stored with Stripe — we never see your account number.
            </p>
            <div className="mb-ach-spec-list">
              Same-day deposit · No monthly fees · Encrypted by Stripe
            </div>
            {achConnected ? (
              <div className="mb-ach-connected" style={{ marginTop: 16 }}>
                ✓ Bank connected · {achConnected.bankName} ··••{achConnected.last4}
                <button
                  type="button"
                  onClick={disconnectAch}
                  className="sig-resign-link"
                  style={{ marginLeft: 12 }}
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="mb-ach-connect-btn"
                onClick={connectAch}
                disabled={achBusy}
                style={{ marginTop: 16 }}
              >
                {achBusy ? 'Connecting…' : 'Connect your bank →'}
              </button>
            )}
            {achError && (
              <div className="field-error" style={{ marginTop: 10 }}>
                {achError}
              </div>
            )}
          </div>

          <div className="packages-section">
            <div className="section-heading">Your subscription packages</div>
            <p className="packages-subtitle">
              Create one package each for men and women, with monthly and annual pricing. We securely collect and manage recurring billing on your behalf.
            </p>
            <PackageRow
              control={control}
              errors={errors}
              group="female"
              label="Female"
            />
            <PackageRow
              control={control}
              errors={errors}
              group="male"
              label="Male"
            />
          </div>

          <div className="step-section">
            <div className="section-heading">When does billing start?</div>
            <p className="field-helper" style={{ marginBottom: 16 }}>
              Choose when a patient's subscription begins after they enroll. This is set during onboarding — to adjust later, reach out to support@brite.live. Patients with completed visits are always charged immediately, regardless of this setting.
            </p>

            <div className="trigger-cards">
              <button
                type="button"
                className={`trigger-card${triggerMode === 'immediate' ? ' is-selected' : ''}`}
                onClick={() => setTriggerMode('immediate')}
              >
                <span className="trigger-card-icon">⚡</span>
                <span className="trigger-card-label">Immediate</span>
                <p className="trigger-card-desc">
                  Card is charged as soon as the patient completes enrollment in
                  the Brite app.
                </p>
                <span className="trigger-card-default">Recommended</span>
              </button>

              <button
                type="button"
                className={`trigger-card${triggerMode === 'first_billable_action' ? ' is-selected' : ''}`}
                onClick={() => setTriggerMode('first_billable_action')}
              >
                <span className="trigger-card-icon">🔄</span>
                <span className="trigger-card-label">First billable action</span>
                <p className="trigger-card-desc">
                  No charge at enrollment. Subscription starts automatically when
                  their provider places the first lab order or writes the first
                  prescription.
                </p>
              </button>
            </div>

          </div>

        </>
      )}

      {!mbEnabled && (
        <div className="mb-skip-detail">
          <div className="mb-skip-detail-headline">
            You'll handle patient billing yourself.
          </div>
          <ul className="mb-skip-detail-list">
            <li>Connect your own processor (Square, Stripe, QuickBooks)</li>
            <li>Reconcile BRITE charges with patient payments each month</li>
            <li>Handle failed cards directly with patients</li>
            <li>Manage compliance and chargebacks separately</li>
          </ul>
          <button
            type="button"
            className="mb-skip-recover"
            onClick={() => setMbEnabled(true)}
          >
            Actually, let me try Managed Billing →
          </button>
        </div>
      )}
      </Band>
      </StepLayout>

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
